import { useFlags } from "launchdarkly-react-client-sdk";
import { Badge } from "../../../../../components/Badge/Badge.tsx";
import { InputNumber } from "../../../../../components/InputNumber/InputNumber.tsx";
import { Slider } from "../../../../../components/Slider/Slider.tsx";
import { Switch } from "../../../../../components/Switch/Switch.tsx";
import { Toggle } from "../../../../../components/Toggle/Toggle.tsx";
import { DEFAULT_LORA_SCALE } from "../../../../boardState/stores/selectedModels/constants.ts";
import type { ModelGenerationParams } from "../../../../boardState/stores/selectedModels/types.ts";
import { useDebugMode } from "../../../../components/DebugMode/hooks/useDebugMode.ts";
import type { ModelWithIsCompatibleAndTags } from "../../../../types.ts";
import {
  BACKGROUND_REPLACE_GENERATION_TYPE,
  BACKGROUND_REPLACE_QUALITY_PRESETS,
  BACKGROUND_REPLACE_QUALITY_PRESETS_WITHOUT_CUSTOM,
  BACKGROUND_REPLACEMENT_MODES,
  LORA_GENERATION_TYPE,
  OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED,
  OBJECT_RERENDERED_QUALITY_PRESETS,
  REFRAME_GENERATION_TYPE,
  ZERO_SHOT_CONSISTENCY_PRESETS,
} from "../../../constants.ts";
import { BACKGROUND_REPLACEMENT_MODE_TO_LABEL } from "../../../display.ts";
import type {
  BackgroundReplaceQualityPreset,
  ModelActions,
} from "../../../types.ts";
import { PanelSection } from "../PanelSection.tsx";
import { PanelSubsection } from "../PanelSubsection.tsx";
import { SwitchSubsection } from "../SwitchSubsection.tsx";

export const ModelSettingsMenu = ({
  model,
  generationParams,
  modelActions,
}: {
  model: ModelWithIsCompatibleAndTags;
  generationParams: ModelGenerationParams;
  modelActions: ModelActions;
}) => {
  const flags = useFlags();
  const onLoraScaleChange = (loraScalePercentage: number) => {
    modelActions.updateModelGenerationParams(model.uuid, {
      scale: loraScalePercentage / 100,
    });
  };

  const isDebugModeOn = useDebugMode();

  const allowedBackgroundReplaceGenerationTypes = (
    isDebugModeOn
      ? [...BACKGROUND_REPLACE_QUALITY_PRESETS]
      : [...BACKGROUND_REPLACE_QUALITY_PRESETS_WITHOUT_CUSTOM]
  ) as BackgroundReplaceQualityPreset[];

  return (
    <div className="w-[256px] p-300 flex-col">
      <div className="flex-row justify-end">
        <Badge
          size="sm"
          color={model.is_trained ? "emphasis" : "cautionSecondary"}
          label={model.is_trained ? "Trained model" : "Zero-shot model"}
        />
      </div>
      <div className="flex-col divide-y">
        {generationParams.model_generation_type === LORA_GENERATION_TYPE ? (
          <PanelSection
            title="Influence"
            rightItem={
              <InputNumber
                value={Math.round(generationParams.scale * 100)}
                onChange={onLoraScaleChange}
                className="w-[56px] label-md-defaul text-primary"
                suffix="%"
              />
            }
            variant="secondary"
          >
            <Slider
              value={Math.round(generationParams.scale * 100)}
              min={0}
              max={100}
              onChange={onLoraScaleChange}
              markValue={DEFAULT_LORA_SCALE * 100}
              className="flex-fill"
              displayPercentage
              displayNumber={false}
            />
          </PanelSection>
        ) : (generationParams.model_generation_type ===
            REFRAME_GENERATION_TYPE ||
            generationParams.model_generation_type ===
              BACKGROUND_REPLACE_GENERATION_TYPE) &&
          flags["objectConsistencyBetaProgram"] ? (
          <>
            <PanelSection title="Consistency">
              <Toggle
                items={ZERO_SHOT_CONSISTENCY_PRESETS.map((item) => (
                  <div key={item} className="capitalize">
                    {item}
                  </div>
                ))}
                index={ZERO_SHOT_CONSISTENCY_PRESETS.indexOf(
                  generationParams.model_generation_type ===
                    BACKGROUND_REPLACE_GENERATION_TYPE
                    ? "HIGH"
                    : "LOW",
                )}
                onIndexChange={(index) => {
                  if (index === ZERO_SHOT_CONSISTENCY_PRESETS.indexOf("HIGH")) {
                    modelActions.updateModelGenerationType(
                      model.uuid,
                      model.is_trained,
                      BACKGROUND_REPLACE_GENERATION_TYPE,
                    );
                  } else {
                    if (
                      generationParams.model_generation_type ===
                      BACKGROUND_REPLACE_GENERATION_TYPE
                    ) {
                      modelActions.updateModelGenerationType(
                        model.uuid,
                        model.is_trained,
                        REFRAME_GENERATION_TYPE,
                      );
                    }
                  }
                }}
              />
            </PanelSection>
            {isDebugModeOn && (
              <PanelSection
                title="Object rerendering"
                infoContent={
                  <div className="flex-col py-100 gap-400 text-primary body-md-default">
                    If enabled, the object will be rerendered.
                  </div>
                }
                rightItem={
                  <Switch
                    value={
                      generationParams.object_rerendered_quality_preset !==
                      OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED
                    }
                    onChange={(isObjectRerendered) =>
                      modelActions.updateModelGenerationParams(model.uuid, {
                        object_rerendered_quality_preset: isObjectRerendered
                          ? OBJECT_RERENDERED_QUALITY_PRESETS[0]
                          : OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED,
                      })
                    }
                  />
                }
              >
                {generationParams.object_rerendered_quality_preset !==
                  OBJECT_RERENDERED_QUALITY_PRESET_NOT_APPLIED && (
                  <PanelSubsection
                    title="Quality preset"
                    infoContent={
                      <div className="flex-col py-100 gap-400 text-primary body-md-default">
                        Finegrain quality preset for the object rerendering.
                      </div>
                    }
                  >
                    <Toggle
                      items={OBJECT_RERENDERED_QUALITY_PRESETS.map((item) => (
                        <div key={item} className="capitalize">
                          {item}
                        </div>
                      ))}
                      index={OBJECT_RERENDERED_QUALITY_PRESETS.indexOf(
                        generationParams.object_rerendered_quality_preset,
                      )}
                      onIndexChange={(index) =>
                        modelActions.updateModelGenerationParams(model.uuid, {
                          object_rerendered_quality_preset:
                            OBJECT_RERENDERED_QUALITY_PRESETS[index],
                        })
                      }
                    />
                  </PanelSubsection>
                )}
              </PanelSection>
            )}
          </>
        ) : null}
        {generationParams.model_generation_type ===
          BACKGROUND_REPLACE_GENERATION_TYPE && (
          <>
            <PanelSection
              title="Resize factor"
              infoContent={
                <div className="flex-col py-100 gap-400 text-primary body-md-default">
                  The resize factor will be used to rescale the product inside
                  the image.
                </div>
              }
            >
              <Slider
                min={10}
                max={100}
                value={Math.round(generationParams.resize_factor * 100)}
                onChange={(resizeFactor) => {
                  modelActions.updateModelGenerationParams(model.uuid, {
                    resize_factor: resizeFactor / 100,
                  });
                }}
                className="flex-fill"
                displayPercentage
                displayNumber={false}
              />
            </PanelSection>
            <PanelSection title="Quality">
              <div className="flex-col gap-200">
                <Toggle
                  items={allowedBackgroundReplaceGenerationTypes.map((item) => (
                    <div key={item} className="capitalize">
                      {item}
                    </div>
                  ))}
                  index={
                    !allowedBackgroundReplaceGenerationTypes.includes(
                      generationParams.background_replace_quality_preset,
                    )
                      ? 0
                      : allowedBackgroundReplaceGenerationTypes.indexOf(
                          generationParams.background_replace_quality_preset,
                        )
                  }
                  onIndexChange={(index) => {
                    modelActions.updateModelGenerationParams(model.uuid, {
                      background_replace_quality_preset:
                        allowedBackgroundReplaceGenerationTypes[index],
                    });
                  }}
                />
                {generationParams.background_replace_quality_preset ===
                  "CUSTOM" && (
                  <div className="flex-col gap-200">
                    <PanelSubsection
                      title="Background replacement mode"
                      infoContent={
                        <div className="flex-col py-100 gap-400 text-primary body-md-default">
                          The background replacement mode will be used to
                          replace the background of the product. Can be either
                          Ideogram or Flux.
                        </div>
                      }
                    >
                      <Toggle
                        items={BACKGROUND_REPLACEMENT_MODES.map((item) => (
                          <div key={item} className="capitalize">
                            {BACKGROUND_REPLACEMENT_MODE_TO_LABEL[item]}
                          </div>
                        ))}
                        index={BACKGROUND_REPLACEMENT_MODES.indexOf(
                          generationParams.background_replacement_mode,
                        )}
                        onIndexChange={(index) => {
                          modelActions.updateModelGenerationParams(model.uuid, {
                            background_replacement_mode:
                              BACKGROUND_REPLACEMENT_MODES[index],
                          });
                        }}
                      />
                    </PanelSubsection>
                    <SwitchSubsection
                      switchName="Upscale"
                      infoContent={
                        <div className="flex-col py-100 gap-400 text-primary body-md-default">
                          If enabled, the image will be upscaled before
                          rerendering the object.
                        </div>
                      }
                      value={generationParams.is_upscaled}
                      onChange={(isUpscaled) =>
                        modelActions.updateModelGenerationParams(model.uuid, {
                          is_upscaled: isUpscaled,
                        })
                      }
                    />
                  </div>
                )}
              </div>
            </PanelSection>
          </>
        )}
      </div>
    </div>
  );
};
